import React from 'react';
import PropTypes from 'prop-types';

import _isObject from 'lodash/isObject';
import _assign from 'lodash/assign';
import _isString from 'lodash/isString';
import _truncate from 'lodash/truncate';

/**
 * Execute a regex on a string to find link to replace:
 * Lorem ipsum [dolar]({0}) sit amet
 * Will match "[dolar]({0})"
 * @type {{findLink: (function(*=))}}
 */
const LINK_REGEX = /\[([\w\d\s?.]+)\]\(\{?([\w\d\s?:/.@#]+)\}?\)/gm;
function findLinks(str) {
  return str.match(LINK_REGEX);
}

/**
 * Extract the text from a markdown style anchor:
 * Input: [Lorem]({0})
 * Result: Lorem
 * @param str
 * @returns {Array|{index: number, input: string}}
 */
function extractTextFromLink(str) {
  const regex = /\[([\w\d\s?.]+)\]\(\{?([\w\d\s?:/.@#]+)\}?\)/gm;
  return regex.exec(str);
}

/**
 * Split a string a multiple points
 * where is matches the link regex
 * @param str
 * @returns {*|Array}
 */
function explode(str) {
  return str.split(/\[[\w\d\s?.]+\]\([{}\w\d\s?:/.@#]+\)/);
}

const RE_EXTERNAL_LINK = /^https?:\/\//;
export function isExternalLink(href) {
  return RE_EXTERNAL_LINK.test(href);
}

/**
 * More tricky than it looks.
 * http://stackoverflow.com/questions/46155/validate-email-address-in-javascript#46181
 * What we do here is to run a very basic validation as emails like 'bob@home@hello' are, surprisingly, correct
 */
const RE_EMAIL = /\S+@\S+/;
export function isEmail(text) {
  return RE_EMAIL.test(text);
}

export const Anchor = props => {
  const isExternal = isExternalLink(props.href);
  const anchorProps = {};
  if (isExternal) {
    anchorProps.target = '_blank';
    anchorProps.rel = 'noopener noreferrer';
  }
  return (
    <a href={props.href} {...anchorProps}>
      {props.children}
    </a>
  );
};

Anchor.propTypes = {
  href: PropTypes.string,
  children: PropTypes.node,
};

function getComponent(linkObject, creator) {
  if (creator) {
    return creator(linkObject);
  }
  return (
    <Anchor href={linkObject.href} key={linkObject.index || 0}>
      {linkObject.text}
    </Anchor>
  );
}

/**
 * Compiles a markdown-like string to an array includes string|object
 *
 * e.g. '[link1]({0}) some text [link2](google.com)'
 *       [{href: '/#/dashboard', text: link1}, ' some text ', {href: 'google.com', text: 'link2'}]
 *
 * @param  {string} str   - the string to be compiled
 * @param  {Array}  links - links to populate for syntax like `[link1]({0})` whereas the `0` indicates the index
 * @return {Array<string|Object>} - the compiled array
 */
export function compileLinks(str = '', links = []) {
  const matchedLinks = findLinks(str);
  // no link at all, return a plain array with only 1 string
  if (!matchedLinks) return [str];

  // e.g. '[link1]({0}) texts [link2]({1}) text3'
  // becomes ['', ' texts ', 'text3'];
  const matchedString = explode(str);

  // e.g. ['[link1]({0})', '[link2](google.com)']
  return (
    matchedLinks
      // [
      //   ['', {type: 'link', href: '/#/dashboard', text: 'link1'}],
      //   [' texts ', {type: 'link', href: 'google.com', text: link2}]]
      // ]
      .map((link, index) => {
        // [link1](google.com)
        // extracted[1] = link1
        // extracted[2] = google.com
        // [link2]({0})
        // extracted[1] = link2
        // extracted[2] = 0
        const extracted = extractTextFromLink(link);

        // [link1](google.com) => NaN
        // [link2]({0}) => 0
        const linkNum = parseInt(extracted[2], 10);
        const linkObj = {
          type: 'link',
          text: extracted[1],
          // [link1](google.com) => google.com
          // [link2]({0}) => link[0]
          href: Number.isNaN(linkNum) ? extracted[2] : links[linkNum],
        };
        // zip the text into the links
        // ['', {type: 'link', href: '/#/dashboard', text: 'link1'}]
        return [matchedString[index], linkObj];
      })
      // concat with trailing text
      // [
      //   ['', {type: 'link', href: '/#/dashboard', text: 'link1'}],
      //   [' texts ', {type: 'link', href: 'google.com', text: link2}]]
      //   ['text3']
      // ]
      .concat(matchedString.slice(-1))
      // flatten
      // [
      //  '',
      //  {type: 'link', href: '/#/dashboard', text: 'link1'},
      //  ' texts ',
      //  {type: 'link', href: 'google.com', text: link2}
      //  'text3'
      // ]
      .reduce((pre, cur) => pre.concat(cur))
      // filter out empty strings
      // [
      //  {type: 'link', href: '/#/dashboard', text: 'link1'},
      //  ' texts ', {type: 'link', href: 'google.com', text: link2}
      //  'text3'
      // ]
      .filter(segment => segment)
  );
}

/**
 * Compiles \n to {type: 'br'}
 *
 * @param  {String} str - the segment to be compiled
 * @return {Array}   - combination of string and {type: 'br'}
 */
export function compileBrs(str) {
  if (str.indexOf('\n') === -1) return [str];
  // 'i am a string \n with some \n and \n'
  // => ['i am a string ', ' with some ', ' and ', '']
  return (
    str
      .split('\n')
      // => [['i am a string ', {type: 'br'}], ['with some ', {type: 'br'}], [' and ', {type: 'br'}], [null]]
      .map((seg, index) => {
        if (index === 0) return [seg];
        if (seg === '') return [{ type: 'br' }];
        return [{ type: 'br' }, seg];
      })
      // => ['i am a string ', {type: 'br'}, 'with some ', {type: 'br'}, ' and ', {type: 'br'}, null]
      .reduce((pre, cur) => pre.concat(cur))
      // => ['i am a string ', {type: 'br'}, 'with some ', {type: 'br'}, ' and ', {type: 'br'}]
      .filter(seg => seg)
  );
}

/**
 * Replace all occurrences of markdown style links
 * with the links given
 *
 * @param {Object} opts
 * @param {string} opts.str - The input string containing markdown style links
 * @param {Array} opts.links - An array of urls to map with the links
 * @param {Array} opts.componentCreator - A function that returns a component to use instead of the default
 * @returns {Array|string}
 */
export function replaceWithLinks(opts) {
  const result = compileLinks(opts.str, opts.links)
    .map(segment => {
      if (_isString(segment)) {
        return compileBrs(segment);
      }
      return [segment];
    })
    // flatten
    .reduce((pre, cur) => pre.concat(cur))
    .map((segment, index) => {
      if (_isObject(segment)) {
        switch (segment.type) {
          case 'link':
            return getComponent(
              _assign({ index }, segment),
              opts.componentCreator
            );
          // case 'br':
          //   return <br key={ index } />;
          default:
            return null;
        }
      }
      return segment;
    });
  // support old syntax, if the only element in the array is string then return the string directly
  // ['string'] => 'string'
  if (result.length === 1 && _isString(result[0])) {
    return result[0];
  }
  return result;
}

/**
 * test if a string includes html tags
 *
 * @param  {string}  str - the string to be tested
 * @return {Boolean}
 */
export function includesHtml(str) {
  return /<.+>.*<\/.+>|<.+\/>/gm.test(str);
}

/**
 * check if the string includes a markdown-like syntax
 *
 * @param  {string} str - string to be tested
 * @return {Boolean}
 */
export function includesLink(str) {
  return LINK_REGEX.test(str);
}

/**
 * Transfers a number to money format
 * use a regex to fomat 123123123.00 => 123,123,123.00
 *
 * @param  {number|string} potentialNumber - string or number that could be parsed to number
 * @return {string}                        - formated string in money format
 */
export function toMoneyFormat(potentialNumber) {
  let number;
  try {
    number = parseFloat(potentialNumber, 10);
  } catch (e) {
    throw new Error(
      `[toMoneyFormat]: parameter must be convertable to number. ${potentialNumber} is not`
    );
  }

  // http://stackoverflow.com/questions/149055/how-can-i-format-numbers-as-money-in-javascript
  return number.toFixed(2).replace(/(\d)(?=(\d{3})+\.)/g, '$1,');
}

export function stripHtml(str) {
  return str.replace(/(<([^>]+)>)/gi, '');
}

/*
 * Extract script tags to be executed from a html string.
 * E.g.
 * '<div></div><script src='//script.js'></script>
 * <script>console.log('hi')</script>'
 * => [{src: '//script.js'}, {text: 'console.log('hi')'}]
 *
 * @param {string} html - string used to set innerHTML, containing single or multiple script tags
 * @return {Array|Object} - extracted scripts
 */
export function extractScriptTags(html) {
  const SCRIPT_REGEX = /<script[^>]*>([\s\S]*?)<\/script>/gi;
  const SRC_REGEX = /src\s*=\s*("([^"]+")|'[^']+')/;

  const scripts = [];
  let script = SCRIPT_REGEX.exec(html);
  while (script !== null) {
    const src = SRC_REGEX.exec(script[0]);
    const scriptObj = {};
    if (src) {
      scriptObj.src = src[1].replace(/"|'/g, '');
    } else {
      scriptObj.text = script[1];
    }
    scripts.push(scriptObj);

    script = SCRIPT_REGEX.exec(html);
  }

  return scripts;
}

/**
 * Truncate a string
 * @param {String} str - The string to shorten
 * @param {Object|Number} opts - The number of chars or a config object that can be passed to _.truncate
 */
export function shorten(str, opts) {
  if (typeof opts === 'number') {
    return _truncate(str, {
      length: opts,
    });
  }
  return _truncate(str, opts);
}

/**
 * Returns just the first 6 and last 4 characters of a string, separated by an
 * ellipsis, if longer than 12 chars.
 * Otherwise returns the original string
 * @param {String} str - The string that may be truncated
 */
export function middleEllipsis(str = '') {
  return str.length < 12 ? str : `${str.slice(0, 6)}...${str.slice(-4)}`;
}

export const textareaRowRegex = /\r\n|\r|\n/g;

// A locale-aware string comparison with smart numeric ordering so 2 is before 10, etc
export const smartCompare = (a, b) =>
  a.localeCompare(b, undefined, { numeric: true });



// WEBPACK FOOTER //
// ./src/client/src/utils/StringUtils.js